// api/client.ts — thin fetch wrapper
// Reads base URL from VITE_API_BASE env var; auto-attaches JWT from localStorage.

import { streamSSE, type SseMessage, type StreamSseOptions } from '../utils/streamSSE';

const LS_TOKEN_KEY = 'asc_token_v1';  // same scope as state/theme's asc_theme_v1

export type ApiAuthScope = 'student' | 'public';

type InvalidStudentSessionListener = () => void;
const invalidStudentSessionListeners = new Set<InvalidStudentSessionListener>();

export function getToken(): string | null {
  return localStorage.getItem(LS_TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token === null) localStorage.removeItem(LS_TOKEN_KEY);
  else localStorage.setItem(LS_TOKEN_KEY, token);
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** Subscribe to the one-shot transition from an active student token to none. */
export function subscribeToInvalidStudentSession(listener: InvalidStudentSessionListener): () => void {
  invalidStudentSessionListeners.add(listener);
  return () => invalidStudentSessionListeners.delete(listener);
}

/** Apply student-session semantics to the exact token a request sent. */
export function classifyStudentApiResponse(
  response: Pick<Response, 'status'>,
  capturedToken: string | null,
  authScope: ApiAuthScope = 'student',
): void {
  if (
    response.status !== 401
    || authScope !== 'student'
    || capturedToken === null
    || capturedToken === ''
    || getToken() !== capturedToken
  ) {
    return;
  }

  setToken(null);
  for (const listener of invalidStudentSessionListeners) {
    try {
      listener();
    } catch {
      // One consumer must not prevent the token transition or other consumers.
    }
  }
}

// Hardcoded fallback so the app never breaks if the env var isn't baked into the build.
// Staging Worker URL — fine to commit, not a secret. In a PRODUCTION build the
// fallback is a deploy mistake (2026-07-10: a lab Pages build without
// VITE_API_BASE silently talked to staging and every student login failed) —
// scream about it so the next phone test surfaces it immediately.
const DEFAULT_API_BASE = 'https://tutor-gateway-staging.autoscuola-ai.workers.dev';
let warnedMissingApiBase = false;

function getApiBase(): string {
  const base = import.meta.env.VITE_API_BASE || DEFAULT_API_BASE;
  if (!import.meta.env.VITE_API_BASE && import.meta.env.PROD && !warnedMissingApiBase) {
    warnedMissingApiBase = true;
    console.error(
      `[api] VITE_API_BASE was not set at build time — falling back to the STAGING worker (${DEFAULT_API_BASE}). ` +
      'If this is a lab/production deployment, the build is broken: redeploy via `npm run deploy:lab`.'
    );
  }
  return base;
}

function normalizeCapturedStudentToken(token: string | null): string | null {
  return token === '' ? null : token;
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
  authScope: ApiAuthScope = 'student',
): Promise<Response> {
  const capturedToken = authScope === 'student'
    ? normalizeCapturedStudentToken(getToken())
    : null;
  const headers = new Headers(init.headers);
  if (capturedToken) headers.set('Authorization', `Bearer ${capturedToken}`);
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${getApiBase()}${path}`, { ...init, headers });
  // A compliant fetch rejects when aborted, but a response can still win the
  // race (and test/native adapters may resolve late). Never apply global auth
  // or navigation semantics after the caller has cancelled this request.
  init.signal?.throwIfAborted();
  classifyStudentApiResponse(res, capturedToken, authScope);
  // W6 v1.1 Session 2: 423 Locked → student account is paused. The student
  // app redirects to /pausa so they see a friendly Italian "tu" landing
  // instead of the underlying error. We do this here (not at the React tree)
  // so even fetches outside an error boundary route correctly. Skip when
  // we're already on /pausa to avoid a redirect loop.
  if (res.status === 423 && typeof window !== 'undefined' && window.location.pathname !== '/pausa') {
    window.location.assign('/pausa');
  }
  return res;
}

export async function apiJson<T>(
  path: string,
  init: RequestInit = {},
  authScope: ApiAuthScope = 'student',
): Promise<T> {
  const res = await apiFetch(path, init, authScope);
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, body, `API ${res.status} at ${path}`);
  }
  return res.json() as Promise<T>;
}

/** Authenticated student SSE request with the same captured-token classifier as apiFetch. */
export async function* studentApiStream(
  path: string,
  opts: Omit<StreamSseOptions, 'onResponse'> = {},
): AsyncGenerator<SseMessage, void, unknown> {
  const capturedToken = getToken();
  if (!capturedToken) throw new Error('Not authenticated — no JWT in localStorage');

  const headers = {
    ...opts.headers,
    Authorization: `Bearer ${capturedToken}`,
  };
  for await (const message of streamSSE(`${getApiBase()}${path}`, {
    ...opts,
    headers,
    onResponse: (response) => classifyStudentApiResponse(response, capturedToken),
  })) {
    yield message;
  }
}
